import { PROPERTIES, RECESSION_LABEL_TEXT } from './plot-bands.constants';

/**
 * The colors available to be used with plot bands. These colors will be displayed in the color
 * picker component and are not modified for opacity. The colors that are used in the classes on the
 * charts may be modified for opacity.
 */
const COLORS = Object.freeze({
  BARRONS: Object.freeze({
    LIGHT_BLUE: '#eaedf1',
    ALICE_BLUE: '#eef5fd',
    LIGHT_GREEN: '#eff1eb',
  }),
  RECESSION: '#eeeff0',
  WSJ: Object.freeze({
    CHAMPAGNE: '#eee2bd',
    DARK_GRAY: '#bfbfbf',
    GRAY: '#eeeff0',
  }),
});

/**
 * An array of colors for the plot bands. These colors will be used in the color picker component.
 */
export const COLOR_PALLETTE = Object.freeze({
  BARRONS: Object.freeze([
    COLORS.BARRONS.LIGHT_BLUE,
    COLORS.BARRONS.ALICE_BLUE,
    COLORS.BARRONS.LIGHT_GREEN,
  ]),
  WSJ: Object.freeze([COLORS.WSJ.GRAY, COLORS.WSJ.CHAMPAGNE, COLORS.WSJ.DARK_GRAY]),
});

/**
 * The base class name for plot band related classes in the chart.
 */
const PLOT_BAND_CLASS_NAME_PREFIX = 'highcharts-plot-band';

/**
 * An object containing class names for different parts of the plot bands on the chart.
 */
export const PLOT_BAND_CLASS_NAME = Object.freeze({
  ANNOTATION: `${PLOT_BAND_CLASS_NAME_PREFIX}--annotation`,
  COLORS: Object.freeze({
    BARRONS: Object.freeze({
      LIGHT_BLUE: `${PLOT_BAND_CLASS_NAME_PREFIX}--light-blue`,
      ALICE_BLUE: `${PLOT_BAND_CLASS_NAME_PREFIX}--alice-blue`,
      LIGHT_GREEN: `${PLOT_BAND_CLASS_NAME_PREFIX}--light-green`,
    }),
    RECESSION: `${PLOT_BAND_CLASS_NAME_PREFIX}--recession`,
    WSJ: Object.freeze({
      CHAMPAGNE: `${PLOT_BAND_CLASS_NAME_PREFIX}--champagne`,
      DARK_GRAY: `${PLOT_BAND_CLASS_NAME_PREFIX}--dark-gray`,
      GRAY: `${PLOT_BAND_CLASS_NAME_PREFIX}--gray`,
    }),
  }),
});

/**
 * An object containing key value pairs of colors and their corresponding class names in the chart.
 * When a user selects a color with the color picker component, we want to map that color onto one
 * of these values to apply that to the chart.
 */
const MAP_COLOR_TO_CLASSNAME = Object.freeze({
  BARRONS: Object.freeze({
    [COLORS.BARRONS.LIGHT_BLUE]: PLOT_BAND_CLASS_NAME.COLORS.BARRONS.LIGHT_BLUE,
    [COLORS.BARRONS.ALICE_BLUE]: PLOT_BAND_CLASS_NAME.COLORS.BARRONS.ALICE_BLUE,
    [COLORS.BARRONS.LIGHT_GREEN]: PLOT_BAND_CLASS_NAME.COLORS.BARRONS.LIGHT_GREEN,
  }),
  RECESSION: Object.freeze({
    [COLORS.RECESSION]: PLOT_BAND_CLASS_NAME.COLORS.RECESSION,
  }),
  WSJ: Object.freeze({
    [COLORS.WSJ.GRAY]: PLOT_BAND_CLASS_NAME.COLORS.WSJ.GRAY,
    [COLORS.WSJ.CHAMPAGNE]: PLOT_BAND_CLASS_NAME.COLORS.WSJ.CHAMPAGNE,
    [COLORS.WSJ.DARK_GRAY]: PLOT_BAND_CLASS_NAME.COLORS.WSJ.DARK_GRAY,
  }),
});

/**
 * An object containing key value pairs of classNames and their corresponding colors. When we group
 * together plot bands, we need to extract the color from the class names.
 */
const MAP_CLASSNAME_TO_COLOR = Object.freeze({
  BARRONS: Object.freeze({
    [PLOT_BAND_CLASS_NAME.COLORS.BARRONS.LIGHT_BLUE]: COLORS.BARRONS.LIGHT_BLUE,
    [PLOT_BAND_CLASS_NAME.COLORS.BARRONS.ALICE_BLUE]: COLORS.BARRONS.ALICE_BLUE,
    [PLOT_BAND_CLASS_NAME.COLORS.BARRONS.LIGHT_GREEN]: COLORS.BARRONS.LIGHT_GREEN,
  }),
  RECESSION: Object.freeze({
    [PLOT_BAND_CLASS_NAME.COLORS.RECESSION]: COLORS.RECESSION,
  }),
  WSJ: Object.freeze({
    [PLOT_BAND_CLASS_NAME.COLORS.WSJ.GRAY]: COLORS.WSJ.GRAY,
    [PLOT_BAND_CLASS_NAME.COLORS.WSJ.CHAMPAGNE]: COLORS.WSJ.CHAMPAGNE,
    [PLOT_BAND_CLASS_NAME.COLORS.WSJ.DARK_GRAY]: COLORS.WSJ.DARK_GRAY,
  }),
});

/**
 * To extract color and class name information, we need to use a special key. This function
 * generates a key for mapping colors on to class names or class names onto colors.
 *
 * @param {Publications} currentProperty The publication of the chart
 * @returns {string}
 */
export const getPublicationKey = (currentProperty) => {
  if (currentProperty) {
    if (currentProperty === PROPERTIES.WSJ || currentProperty === PROPERTIES.DJ_RISKJOURNAL) return PROPERTIES.WSJ.toUpperCase();
    return PROPERTIES.BARRONS.toUpperCase();
  }

  // if no property is provided, get the key for recessions
  return RECESSION_LABEL_TEXT.EN;
};

/**
 * Given a hex color and a key, get the corresponding class name for the color.
 *
 * @param {string} color The color of a plot band
 * @param {Publications} currentProperty The publication of the chart
 * @returns {string} The class name of a plot band
 */
export const getClassName = (color, currentProperty = null) => {
  const key = getPublicationKey(currentProperty);

  return MAP_COLOR_TO_CLASSNAME[key][color];
};

/**
 * Given a class name and a key, get the corresponding color for the class name.
 *
 * @param {string} className The class name of a plot band
 * @param {Publications} currentProperty The publication of the chart
 * @returns {string} The color of a plot band
 */
export const getColor = (className, currentProperty = null) => {
  const key = getPublicationKey(currentProperty);

  return MAP_CLASSNAME_TO_COLOR[key][className];
};

/**
 * Given a hexcode string, determine whether or not `color` is the color used for recessions bands.
 *
 * @param {string} str A hexcode string that represents a color or a className
 * @returns {boolean} A flag that indicates whether or not `color` is the color of the recessions
 * bands
 */
export const isColorDuplicate = (str = '', currentProperty) => {
  const key = getPublicationKey(currentProperty);

  // str is hexcode
  if (str[0] === '#') {
    return str === COLORS.RECESSION;
  }

  // str is className
  return MAP_CLASSNAME_TO_COLOR[key][str] === COLORS.RECESSION;
};
